import "../teamList/index.scss";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import AppShell from "../../components/appShell";
import TeamCards from "../../components/teamCards";
import {
  teams as getTeams,
  selectTeamsData,
  selectTeamsLoading,
} from "../../store/features/generalSlice";
import { useSocketRoom } from "../../hooks/useSocketRoom";

// The public Teams board. Nobody is signed in, so the shell shows the top bar
// and no rail, and the session id in the URL does the work a login does
// elsewhere: it names the auction and scopes everything to it.
const TeamListGeneral = ({ socket }) => {
  const { sessionId } = useParams();
  const dispatch = useDispatch();
  const teams = useSelector(selectTeamsData);
  const loading = useSelector(selectTeamsLoading);

  // Rejoins on every reconnect, so a dropped socket recovers on its own.
  useSocketRoom(socket, { role: "public", id: null, sessionId });

  useEffect(() => {
    if (!sessionId) return;
    dispatch(getTeams(sessionId));
  }, [sessionId]);

  // Squads only change when a lot closes, so the room's own events are enough
  // to keep this current without anyone reloading.
  useEffect(() => {
    if (!sessionId || !socket) return;

    const refresh = () => dispatch(getTeams(sessionId));

    socket.on("set-player-sold", refresh);
    socket.on("set-player-unsold", refresh);
    socket.on("block-finished", refresh);
    socket.on("block-started", refresh);

    return () => {
      socket.off("set-player-sold", refresh);
      socket.off("set-player-unsold", refresh);
      socket.off("block-finished", refresh);
      socket.off("block-started", refresh);
    };
  }, [sessionId, socket]);

  if (!sessionId) {
    return (
      <AppShell role="public" label="Teams">
        <h1 className="au-heading">Teams</h1>
        <p className="au-subheading">
          Open this page from the live board, or use the link the organiser sent
          you. It needs an auction to show.
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell
      role="public"
      label="Teams"
      aside={
        <>
          <Link className="au-btn ghost small" to={`/playerlist/${sessionId}`}>
            Auction pool
          </Link>
          <Link className="au-btn ghost small" to={`/live/${sessionId}`}>
            Back to the live board
          </Link>
        </>
      }
    >
      <header className="team-list-head">
        <div>
          <h1 className="au-heading">Teams</h1>
          <p className="au-subheading">
            Every squad as it stands, by role. Open slots are the ones still to
            fill.
          </p>
        </div>
      </header>

      {teams?.length ? (
        <div className="team-grid">
          {teams.map((team) => (
            <TeamCards
              key={team?._id || team?.name}
              team={team?.team}
              teamColor={team?.color?.hex}
              secondaryTeamColor={team?.secondaryColor}
              teamName={team?.name}
              filteredPlayerData={team?.filteredPlayers}
            />
          ))}
        </div>
      ) : (
        <p className="au-empty">
          {loading ? "Loading the teams…" : "No teams yet."}
        </p>
      )}
    </AppShell>
  );
};

export default TeamListGeneral;
